import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { call } from "./api";
import { SessionContext, type SessionState } from "./session";

/**
 * The session, read once at boot and refreshed after every ceremony.
 *
 * Alone in its own file: `session.ts` beside it holds the context and the hooks,
 * and a module exporting both a component and a hook breaks Fast Refresh for
 * every consumer of either.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  const refresh = useCallback(async () => {
    const result = await call("GET /v1/me");
    if (result.ok) return setState({ status: "ready", me: result.data });
    // «The api did not answer» and «you are not signed in» are different
    // answers, and the shell acts on them differently: one is a retry, the other
    // is a redirect to the sign-in page.
    if (result.kind === "unreachable") return setState({ status: "unreachable" });
    setState({ status: "anon" });
  }, []);

  const signOut = useCallback(async () => {
    await fetch("/api/session", {
      method: "DELETE",
      headers: { "x-requested-by": "shoppy-admin" },
      credentials: "same-origin",
    }).catch(() => undefined);
    // The local state is cleared whatever the api says: a sign-out that leaves
    // the console drawn because the network was slow is a sign-out that did not
    // happen on the only machine the operator can see.
    setState({ status: "anon" });
  }, []);

  useEffect(() => {
    // Reading who is signed in is a synchronisation with an external system,
    // which is what an effect is for; the lint cannot tell that from a derived
    // render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const value = useMemo(() => ({ state, refresh, signOut }), [state, refresh, signOut]);
  return <SessionContext value={value}>{children}</SessionContext>;
}
